import { supabase } from "./supabase";
import { genererEcritureFactureVenteEnvoyee, genererEcritureFactureVenteAvoirEnvoyee, genererEcritureRecetteFactureVente, genererEcritureRemboursementClient } from "./comptable";
import { logger } from "./logger";
import { arrondirMontant, calculerTVA, decomposerTTC } from "./formatMontant";
import { generateUUID } from "./utils";

/** Ligne facture / devis : HT = Σ(quantité × PU) arrondi par ligne ; TVA = arrondi(HT × taux / 100) ; TTC = HT + TVA. */
export function montantsLigneQuantitePuHt(quantite: number, prixUnitaireHt: number, tauxTva: number) {
  const total_ht = arrondirMontant(Number(quantite) * Number(prixUnitaireHt));
  const total_tva = calculerTVA(total_ht, Number(tauxTva) || 0);
  const total_ttc = total_ht + total_tva;
  return { total_ht, total_tva, total_ttc };
}

/** À partir du TTC et du taux : HT arrondi, TVA = TTC − HT (entiers FCFA). */
export function montantsDepuisTTC(montantTTC: number, tauxTva: number) {
  return decomposerTTC(montantTTC, Number(tauxTva) || 0);
}

export type FactureStatut = "brouillon" | "a_envoyer" | "envoye" | "a_venir" | "en_retard" | "encaissee";

export interface Client {
  id: string;
  dossier_id: string;
  nom: string;
  email?: string | null;
  telephone?: string | null;
  adresse?: string | null;
  ville?: string | null;
  ncc?: string | null;
  raison_sociale?: string | null;
  /** Régime fiscal (logique AIRSI, fiche client) */
  regime_fiscal?: string | null;
  regime_imposition?: string | null;
  numero_tiers?: string | null;
  code_comptable?: string | null;
  /** Compte auxiliaire 8 chiffres (synchronisé côté serveur) */
  numero_compte?: string | null;
  compte_auxiliaire_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface FactureLigne {
  id: string;
  facture_id: string;
  libelle: string;
  reference?: string | null;
  quantite: number;
  unite: string;
  prix_unitaire_ht: number;
  taux_tva: number;
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  ordre: number;
}

export interface Facture {
  id: string;
  dossier_id: string;
  client_id: string;
  numero: string;
  date_emission: string;
  date_echeance: string;
  statut: FactureStatut;
  montant_ht: number;
  montant_tva: number;
  montant_airsi?: number;
  taux_airsi?: number;
  montant_ttc: number;
  restant_du_ttc: number;
  libelle?: string | null;
  delai_paiement?: string | null;
  mode_paiement?: string | null;
  devis_numero?: string | null;
  langue?: string | null;
  moyens_paiement?: string[] | null;
  facture_origine_id?: string | null;
  facture_origine_numero?: string | null;
  created_at?: string;
  updated_at?: string;
  share_token?: string | null;
  /** Numéro FNE officiel 20 caractères (DGI) */
  numero_fne?: string | null;
  client?: Client | null;
  facture_lignes?: FactureLigne[];
}

export interface FactureWithClient extends Facture {
  client: Client | null;
}

/** Liste des clients d'un dossier */
export async function fetchClients(dossierId: string): Promise<Client[]> {
  const { data, error } = await supabase
    .from("clients")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("nom");
  if (error) throw error;
  return data || [];
}

/** Libellé FNE / ancien champ à partir du régime fiscal structuré */
function regimeFiscalVersImpositionFne(regime: string | null | undefined): string | null {
  if (!regime) return null;
  switch (regime) {
    case "rni":
      return "RNI";
    case "rsi":
      return "RS";
    case "micro":
      return "CF";
    case "tee":
      return "TEE";
    case "informel":
      return "Autre";
    default:
      return null;
  }
}

/** Met à jour un client */
export async function updateClient(
  clientId: string,
  input: {
    nom?: string;
    email?: string;
    telephone?: string;
    adresse?: string;
    ville?: string;
    ncc?: string;
    regime_fiscal?: string;
    regime_imposition?: string;
  }
): Promise<Client> {
  const payload: Record<string, unknown> = {};
  if (input.nom !== undefined) payload.nom = input.nom.trim();
  if (input.email !== undefined) payload.email = input.email || null;
  if (input.telephone !== undefined) payload.telephone = input.telephone || null;
  if (input.adresse !== undefined) payload.adresse = input.adresse || null;
  if (input.ville !== undefined) payload.ville = input.ville || null;
  if (input.ncc !== undefined) payload.ncc = input.ncc || null;
  if (input.regime_fiscal !== undefined) {
    payload.regime_fiscal = input.regime_fiscal || "inconnu";
    payload.regime_imposition = regimeFiscalVersImpositionFne(input.regime_fiscal || "inconnu");
  } else if (input.regime_imposition !== undefined) {
    payload.regime_imposition = input.regime_imposition || null;
  }
  const { data, error } = await supabase.from("clients").update(payload).eq("id", clientId).select().single();
  if (error) throw error;
  return data;
}

/** Crée un client */
export async function createClient(
  dossierId: string,
  input: {
    nom: string;
    email?: string;
    telephone?: string;
    adresse?: string;
    ville?: string;
    ncc?: string;
    regime_fiscal?: string;
    regime_imposition?: string;
  }
): Promise<Client> {
  const regimeFisc = input.regime_fiscal || "inconnu";
  const { data, error } = await supabase
    .from("clients")
    .insert({
      dossier_id: dossierId,
      nom: input.nom.trim(),
      email: input.email || null,
      telephone: input.telephone || null,
      adresse: input.adresse || null,
      ville: input.ville || null,
      ncc: input.ncc || null,
      regime_fiscal: regimeFisc,
      regime_imposition: regimeFiscalVersImpositionFne(regimeFisc) ?? input.regime_imposition ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Liste des factures d'un dossier (avec client) */
export async function fetchFactures(
  dossierId: string,
  opts?: {
    statut?: FactureStatut;
    search?: string;
    limit?: number;
    offset?: number;
    dateDebut?: string;
    dateFin?: string;
  }
): Promise<FactureWithClient[]> {
  let q = supabase
    .from("factures")
    .select(`
      *,
      clients (*)
    `)
    .eq("dossier_id", dossierId)
    .order("date_emission", { ascending: false });

  const d0 = opts?.dateDebut?.slice(0, 10);
  const d1 = opts?.dateFin?.slice(0, 10);
  if (d0) q = q.gte("date_emission", d0);
  if (d1) q = q.lte("date_emission", d1);

  if (opts?.statut) q = q.eq("statut", opts.statut);
  if (opts?.search) {
    q = q.or(`numero.ilike.%${opts.search}%,libelle.ilike.%${opts.search}%`);
  }
  if (opts?.limit) q = q.limit(opts.limit);
  if (opts?.offset) q = q.range(opts.offset, opts.offset + (opts.limit || 25) - 1);

  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map((f: Record<string, unknown> & { clients?: Client | null }) => ({
    ...f,
    client: f.clients || null,
  })) as FactureWithClient[];
}

/** Agrégats factures (CA facturé, encaissé, en retard) + effectifs par statut */
export async function fetchFacturesAgregats(
  dossierId: string,
  opts?: { dateDebut?: string; dateFin?: string }
): Promise<{
  caFacture: number;
  caEncaisse: number;
  enRetardCount: number;
  enRetardMontant: number;
  counts: Record<FactureStatut, number>;
}> {
  let q = supabase
    .from("factures")
    .select("statut, montant_ht, montant_ttc, restant_du_ttc, date_emission")
    .eq("dossier_id", dossierId);

  const d0 = opts?.dateDebut?.slice(0, 10);
  const d1 = opts?.dateFin?.slice(0, 10);
  if (d0) q = q.gte("date_emission", d0);
  if (d1) q = q.lte("date_emission", d1);

  const { data: factures, error } = await q;

  if (error) throw error;

  let caFacture = 0;
  let caEncaisse = 0;
  let enRetardCount = 0;
  let enRetardMontant = 0;
  const counts: Record<FactureStatut, number> = {
    brouillon: 0,
    a_envoyer: 0,
    envoye: 0,
    a_venir: 0,
    en_retard: 0,
    encaissee: 0,
  };

  for (const f of factures || []) {
    caFacture += Number(f.montant_ht) || 0;
    if (f.statut === "encaissee") {
      caEncaisse += Number(f.montant_ht) || 0;
    }
    if (f.statut === "en_retard") {
      enRetardCount += 1;
      enRetardMontant += Number(f.montant_ht) || 0;
    }
    if (f.statut && counts[f.statut as FactureStatut] !== undefined) {
      counts[f.statut as FactureStatut] += 1;
    }
  }

  return { caFacture, caEncaisse, enRetardCount, enRetardMontant, counts };
}

/**
 * @legacy Utilisé uniquement pour l'aperçu numéro avoir côté client ; la création réelle utilise le RPC create_facture_vente.
 * Génère le prochain numéro d'avoir (AV-YYYY-NNNNN).
 */
export async function getNextAvoirNumero(dossierId: string): Promise<string> {
  const year = new Date().getFullYear();
  const startOfYear = `${year}-01-01`;
  const { count, error } = await supabase
    .from("factures")
    .select("id", { count: "exact", head: true })
    .eq("dossier_id", dossierId)
    .not("facture_origine_id", "is", null)
    .gte("date_emission", startOfYear);
  if (error) throw error;
  const n = (count ?? 0) + 1;
  return `AV-${year}-${String(n).padStart(5, "0")}`;
}

/**
 * @legacy Utilisé uniquement pour l'aperçu numéro côté client ; la création réelle utilise le RPC create_facture_vente.
 * Génère le prochain numéro de facture (ne persiste pas l'incrément).
 */
export async function getNextFactureNumero(dossierId: string): Promise<string> {
  const { data: config } = await supabase
    .from("dossier_facture_config")
    .select("*")
    .eq("dossier_id", dossierId)
    .maybeSingle();

  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, "0");
  const prefixe = config?.prefixe ?? "FA";
  const suffixe = config?.suffixe ?? "";
  const includeYear = config?.include_year ?? true;
  const longueur = Math.min(10, Math.max(3, Number(config?.longueur_sequence) || 5));
  const resetPeriode = config?.reset_periode ?? "annuel";
  let prochain = Number(config?.prochain_numero) || 1;
  const dernierePeriode = config?.derniere_periode;
  const periodeCourante = resetPeriode === "annuel" ? String(year) : resetPeriode === "mensuel" ? `${year}-${month}` : "all";

  if (dernierePeriode !== periodeCourante) {
    prochain = 1;
  }

  const milieu = includeYear ? (resetPeriode === "mensuel" ? `-${year}${month}-` : `-${year}-`) : "-";
  return `${prefixe}${milieu}${String(prochain).padStart(longueur, "0")}${suffixe}`;
}

/**
 * @legacy Plus utilisé pour la création (RPC create_facture_vente gère l'incrément côté serveur).
 * Incrémente le compteur après création effective d'une facture.
 */
export async function incrementFactureNumero(dossierId: string): Promise<void> {
  const { data: config } = await supabase
    .from("dossier_facture_config")
    .select("*")
    .eq("dossier_id", dossierId)
    .maybeSingle();

  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, "0");
  const resetPeriode = config?.reset_periode ?? "annuel";
  const periodeCourante = resetPeriode === "annuel" ? String(year) : resetPeriode === "mensuel" ? `${year}-${month}` : "all";
  const prochain = (config?.derniere_periode === periodeCourante ? Number(config?.prochain_numero) || 1 : 1) + 1;

  await supabase
    .from("dossier_facture_config")
    .upsert(
      {
        dossier_id: dossierId,
        prefixe: config?.prefixe ?? "FA",
        suffixe: config?.suffixe ?? "",
        include_year: config?.include_year ?? true,
        longueur_sequence: config?.longueur_sequence ?? 5,
        reset_periode: config?.reset_periode ?? "annuel",
        derniere_periode: periodeCourante,
        prochain_numero: prochain,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "dossier_id" }
    );
}

/** Crée une facture avec ses lignes (RPC serveur : numéros et montants en une transaction atomique). */
export async function createFacture(
  dossierId: string,
  input: {
    client_id: string;
    /** Ignoré : le numéro interne est généré côté serveur. */
    numero?: string;
    date_emission: string;
    date_echeance: string;
    libelle?: string;
    delai_paiement?: string;
    mode_paiement?: string;
    statut?: FactureStatut;
    devis_numero?: string;
    langue?: string;
    moyens_paiement?: string[];
    facture_origine_id?: string;
    facture_origine_numero?: string;
    /** Ignoré : le numéro FNE est généré côté serveur. */
    numero_fne?: string;
    lignes: Array<{
      libelle: string;
      reference?: string;
      quantite: number;
      unite?: string;
      prix_unitaire_ht: number;
      taux_tva: number;
    }>;
  }
): Promise<Facture> {
  const isAvoir = Boolean(input.facture_origine_id);
  const { data, error } = await supabase.rpc("create_facture_vente", {
    p_dossier_id: dossierId,
    p_is_avoir: isAvoir,
    p_facture_origine_id: input.facture_origine_id || null,
    p_facture_origine_numero: input.facture_origine_numero || null,
    p_client_id: input.client_id,
    p_date_emission: input.date_emission,
    p_date_echeance: input.date_echeance,
    p_libelle: input.libelle || null,
    p_delai_paiement: input.delai_paiement || "30j",
    p_mode_paiement: input.mode_paiement || null,
    p_statut: input.statut ?? "brouillon",
    p_devis_numero: input.devis_numero || null,
    p_langue: input.langue || "fr",
    p_moyens_paiement: input.moyens_paiement && input.moyens_paiement.length > 0 ? input.moyens_paiement : [],
    p_lignes: input.lignes.map((l) => ({
      libelle: l.libelle,
      reference: l.reference ?? null,
      quantite: Number(l.quantite) || 0,
      unite: l.unite ?? "Unité(s)",
      prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0,
      taux_tva: Number(l.taux_tva) || 0,
    })),
  });
  if (error) {
    const err = error as unknown as Record<string, unknown>;
    const msg =
      [err?.message, err?.details, err?.hint].filter((x) => x != null && String(x).trim()).join(" — ")
      || (typeof err?.error === "object" && err?.error != null && "message" in (err.error as object)
        ? String((err.error as { message?: string }).message)
        : null)
      || (err?.code ? `Erreur ${err.code}` : null);
    if (import.meta.env?.DEV) console.error("[createFacture] Supabase error:", error);
    throw new Error(msg || "Erreur lors de la création de la facture");
  }
  return data as Facture;
}

/** Récupère une facture par id (avec client et lignes) */
export async function fetchFacture(factureId: string): Promise<(Facture & { clients?: Client; facture_lignes?: FactureLigne[] }) | null> {
  const { data, error } = await supabase
    .from("factures")
    .select(`*, clients (*), facture_lignes (*)`)
    .eq("id", factureId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/** Met à jour une facture et ses lignes (RPC serveur : recalcul montants et remplacement des lignes en une transaction). */
export async function updateFacture(
  factureId: string,
  input: {
    client_id: string;
    date_emission: string;
    date_echeance: string;
    libelle?: string;
    mode_paiement?: string;
    statut?: FactureStatut;
    langue?: string;
    moyens_paiement?: string[];
    lignes: Array<{
      libelle: string;
      reference?: string;
      quantite: number;
      unite: string;
      prix_unitaire_ht: number;
      taux_tva: number;
    }>;
  }
): Promise<Facture> {
  const { data, error } = await supabase.rpc("update_facture_vente", {
    p_facture_id: factureId,
    p_client_id: input.client_id,
    p_date_emission: input.date_emission,
    p_date_echeance: input.date_echeance,
    p_libelle: input.libelle ?? null,
    p_mode_paiement: input.mode_paiement ?? null,
    p_statut: input.statut ?? "brouillon",
    p_langue: input.langue ?? null,
    p_moyens_paiement: input.moyens_paiement ?? null,
    p_lignes: input.lignes.map((l) => ({
      libelle: l.libelle,
      reference: l.reference ?? null,
      quantite: Number(l.quantite) || 0,
      unite: l.unite || "Unité(s)",
      prix_unitaire_ht: Number(l.prix_unitaire_ht) || 0,
      taux_tva: Number(l.taux_tva) || 0,
    })),
  });
  if (error) throw error;
  const facture = data as Facture;
  if (input.statut === "envoye") {
    try {
      await (facture.facture_origine_id ? genererEcritureFactureVenteAvoirEnvoyee : genererEcritureFactureVenteEnvoyee)(factureId);
    } catch (e) {
      logger.error("[FACTURATION] Écriture facture/avoir vente envoyé non générée:", e);
    }
  }
  return facture;
}

/** Supprime une facture (brouillon uniquement) et ses lignes */
export async function deleteFacture(factureId: string): Promise<void> {
  const { data: facture, error: fetchErr } = await supabase
    .from("factures")
    .select("statut")
    .eq("id", factureId)
    .single();
  if (fetchErr || !facture) throw new Error("Facture introuvable");
  if (facture.statut !== "brouillon") throw new Error("Seules les factures brouillon peuvent être supprimées");

  await supabase.from("facture_lignes").delete().eq("facture_id", factureId);
  const { error: delErr } = await supabase.from("factures").delete().eq("id", factureId);
  if (delErr) throw delErr;
}

/** Met à jour le statut d'une facture */
export async function updateFactureStatut(factureId: string, statut: FactureStatut): Promise<Facture> {
  const { data, error } = await supabase
    .from("factures")
    .update({ statut, updated_at: new Date().toISOString() })
    .eq("id", factureId)
    .select()
    .single();
  if (error) throw error;
  if (statut === "envoye") {
    try {
      const isAvoir = !!(data as { facture_origine_id?: string | null }).facture_origine_id;
      await (isAvoir ? genererEcritureFactureVenteAvoirEnvoyee : genererEcritureFactureVenteEnvoyee)(factureId);
    } catch (e) {
      logger.error("[FACTURATION] Écriture facture/avoir vente envoyé non générée:", e);
    }
  }
  return data;
}

/** Obtient ou crée le share_token d'une facture pour le lien public */
export async function getOrCreateShareToken(factureId: string): Promise<string> {
  const { data: f, error: fetchErr } = await supabase
    .from("factures")
    .select("share_token")
    .eq("id", factureId)
    .single();
  if (fetchErr || !f) throw new Error("Facture introuvable");
  if (f.share_token) return f.share_token;
  const token = generateUUID();
  const { error: updateErr } = await supabase
    .from("factures")
    .update({ share_token: token })
    .eq("id", factureId);
  if (updateErr) throw updateErr;
  return token;
}

/** Ligne facture vente pour suggestion rapprochement depuis une transaction (entrée) */
export interface FactureVenteRow {
  id: string;
  numero: string;
  montant_ttc: number;
  restant_du_ttc: number;
  date_emission: string;
  client?: { nom: string } | null;
  /** true si avoir sur vente (facture_origine_id non null) */
  isAvoir?: boolean;
}

const MONTANT_TOLERANCE_PCT = 0.05;
const MONTANT_TOLERANCE_MIN = 100;

/** Recherche factures + avoirs pour rapprochement multi. Factures : restant > 0. Avoirs : restant < 0. Montant ±tolerance pour factures ; tous les avoirs disponibles. */
export async function searchFacturesVenteForTransaction(
  dossierId: string,
  montantTtc: number,
  transactionId: string,
  libelleOptional?: string,
  suggestedTier?: string | null
): Promise<FactureVenteRow[]> {
  const tol = Math.max(MONTANT_TOLERANCE_MIN, Math.round(Math.abs(montantTtc) * MONTANT_TOLERANCE_PCT));
  const maxFacture = Math.abs(montantTtc) + tol;
  const maxAvoir = Math.abs(montantTtc) * 2;

  const { data: alreadyLinked } = await supabase
    .from("facture_paiements")
    .select("facture_id")
    .eq("transaction_id", transactionId);
  const linkedIds = new Set((alreadyLinked || []).map((p: { facture_id: string }) => p.facture_id));

  const { data: factures, error: errF } = await supabase
    .from("factures")
    .select("id, numero, montant_ttc, restant_du_ttc, date_emission, facture_origine_id, clients(nom)")
    .eq("dossier_id", dossierId)
    .is("facture_origine_id", null)
    .gt("restant_du_ttc", 0)
    .lte("restant_du_ttc", maxFacture)
    .order("date_emission", { ascending: false });
  if (errF) throw errF;

  const { data: avoirs, error: errA } = await supabase
    .from("factures")
    .select("id, numero, montant_ttc, restant_du_ttc, date_emission, facture_origine_id, clients(nom)")
    .eq("dossier_id", dossierId)
    .not("facture_origine_id", "is", null)
    .lt("restant_du_ttc", 0)
    .gte("restant_du_ttc", -maxAvoir)
    .order("date_emission", { ascending: false });
  if (errA) throw errA;

  const mapRow = (f: { id: unknown; numero: unknown; montant_ttc: unknown; restant_du_ttc: unknown; date_emission: unknown; facture_origine_id?: string | null; clients?: { nom: string }[] | { nom: string } | null }) => {
    const client = Array.isArray(f.clients) ? f.clients[0] ?? null : f.clients ?? null;
    const isAvoir = !!f.facture_origine_id;
    return {
      id: f.id,
      numero: f.numero,
      montant_ttc: Number(f.montant_ttc) || 0,
      restant_du_ttc: Number(f.restant_du_ttc) || 0,
      date_emission: f.date_emission,
      client,
      isAvoir,
    };
  };

  let listF = ((factures || [])
    .map(mapRow)
    .filter((f) => !linkedIds.has(f.id as string)) as FactureVenteRow[]);
  const listA = ((avoirs || []).map(mapRow).filter((f) => !linkedIds.has(f.id as string)) as FactureVenteRow[]);
  let list = [...listF, ...listA];

  if (libelleOptional?.trim() || suggestedTier?.trim()) {
    const q = (libelleOptional || suggestedTier || "").trim().toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);
    const norm = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const match = list.filter((f) => {
      const nom = norm((f.client?.nom ?? "") || "");
      return words.some((w) => nom.includes(norm(w)));
    });
    if (match.length > 0) list = match;
  }
  return list;
}

/** Recherche avoirs éligibles pour rapprochement remboursement client (sortie). Uniquement avoirs. */
export async function searchAvoirsVenteForTransactionSortie(
  dossierId: string,
  montantTtc: number,
  transactionId: string
): Promise<FactureVenteRow[]> {
  const tol = Math.max(MONTANT_TOLERANCE_MIN, Math.round(Math.abs(montantTtc) * MONTANT_TOLERANCE_PCT));
  const maxAvoir = Math.abs(montantTtc) + tol;

  const { data: alreadyLinked } = await supabase
    .from("facture_paiements")
    .select("facture_id")
    .eq("transaction_id", transactionId);
  const linkedIds = new Set((alreadyLinked || []).map((p: { facture_id: string }) => p.facture_id));

  const { data: avoirs, error } = await supabase
    .from("factures")
    .select("id, numero, montant_ttc, restant_du_ttc, date_emission, facture_origine_id, clients(nom)")
    .eq("dossier_id", dossierId)
    .not("facture_origine_id", "is", null)
    .lt("restant_du_ttc", 0)
    .gte("restant_du_ttc", -maxAvoir)
    .order("date_emission", { ascending: false });
  if (error) throw error;

  const mapRow = (f: { id: unknown; numero: unknown; montant_ttc: unknown; restant_du_ttc: unknown; date_emission: unknown; facture_origine_id?: string | null; clients?: { nom: string }[] | { nom: string } | null }) => {
    const client = Array.isArray(f.clients) ? f.clients[0] ?? null : f.clients ?? null;
    return {
      id: f.id,
      numero: f.numero,
      montant_ttc: Number(f.montant_ttc) || 0,
      restant_du_ttc: Number(f.restant_du_ttc) || 0,
      date_emission: f.date_emission,
      client,
      isAvoir: true,
    };
  };

  return ((avoirs || []).map(mapRow).filter((f) => !linkedIds.has(f.id as string)) as FactureVenteRow[]);
}

/** Applique un paiement sur une facture ou utilisation d'un avoir. Avoir : restant remonte vers 0 (restant + montant). */
async function rapprocherUneFactureOuAvoir(
  factureId: string,
  transactionId: string,
  montant: number,
  datePaiement: string
): Promise<void> {
  const dateStr = datePaiement?.slice ? datePaiement.slice(0, 10) : String(datePaiement).slice(0, 10);
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    throw new Error("Date de paiement invalide (attendue: YYYY-MM-DD)");
  }

  const { data: facture, error: fetchErr } = await supabase
    .from("factures")
    .select("restant_du_ttc, montant_ttc, facture_origine_id")
    .eq("id", factureId)
    .single();
  if (fetchErr || !facture) throw new Error("Facture introuvable");

  const restant = Number(facture.restant_du_ttc) || 0;
  const isAvoir = !!facture.facture_origine_id;
  const aPayer = Math.min(Math.abs(montant), isAvoir ? Math.abs(restant) : restant);
  if (aPayer <= 0) throw new Error(isAvoir ? "Avoir déjà entièrement utilisé" : "Facture déjà soldée");

  const { data: paiement, error: insertErr } = await supabase
    .from("facture_paiements")
    .insert({
      facture_id: factureId,
      montant: aPayer,
      date_paiement: dateStr,
      transaction_id: transactionId,
    })
    .select("id")
    .single();
  if (insertErr) throw new Error(insertErr.message || "Erreur lors de l'insertion du paiement");

  if (paiement?.id) {
    const { data: tx } = await supabase.from("transactions").select("type").eq("id", transactionId).single();
    const txType = (tx as { type?: string } | null)?.type;
    if (!isAvoir) {
      try {
        await genererEcritureRecetteFactureVente(paiement.id);
      } catch (e) {
        logger.error("[FACTURATION] Écriture encaissement facture vente non générée:", e);
      }
    } else if (txType === "sortie") {
      try {
        await genererEcritureRemboursementClient(paiement.id);
      } catch (e) {
        logger.error("[FACTURATION] Écriture remboursement client non générée:", e);
      }
    }
  }

  const nouveauRestant = isAvoir ? Math.min(0, restant + aPayer) : Math.max(0, restant - aPayer);
  const payload: { restant_du_ttc: number; statut?: FactureStatut } = { restant_du_ttc: nouveauRestant };
  if (!isAvoir && nouveauRestant < 1) payload.statut = "encaissee";

  const { error: updateErr } = await supabase
    .from("factures")
    .update(payload)
    .eq("id", factureId);
  if (updateErr) throw updateErr;
}

/** Rapproche une facture client avec une transaction (création facture_paiement). Met à jour restant_du_ttc et statut si soldée. */
export async function rapprocherFactureVente(
  factureId: string,
  transactionId: string,
  montant: number,
  datePaiement: string
): Promise<void> {
  await rapprocherUneFactureOuAvoir(factureId, transactionId, montant, datePaiement);
  const { error: txErr } = await supabase
    .from("transactions")
    .update({ statut: "affectee", statut_justification: "justifiee" })
    .eq("id", transactionId);
  if (txErr) throw txErr;
}

/** Rapprochement multi : plusieurs factures et/ou avoirs en une fois. Net attendu = sum(factures) - sum(avoirs). */
export async function rapprocherFacturesVenteMulti(
  transactionId: string,
  allocations: Array<{ factureId: string; montant: number }>,
  datePaiement: string
): Promise<void> {
  if (!allocations.length) throw new Error("Aucune allocation");
  for (const { factureId, montant } of allocations) {
    if (montant <= 0) continue;
    await rapprocherUneFactureOuAvoir(factureId, transactionId, montant, datePaiement);
  }
  const { error: txErr } = await supabase
    .from("transactions")
    .update({ statut: "affectee", statut_justification: "justifiee" })
    .eq("id", transactionId);
  if (txErr) throw txErr;
}

/** IDs des transactions ayant une facture client rapprochée (via facture_paiements). */
export async function fetchTransactionIdsWithFactureVenteRapprochee(): Promise<Set<string>> {
  const { data, error } = await supabase
    .from("facture_paiements")
    .select("transaction_id")
    .not("transaction_id", "is", null);
  if (error) return new Set();
  const ids = (data || [])
    .map((r: { transaction_id: string | null }) => r.transaction_id)
    .filter((id): id is string => !!id);
  return new Set(ids);
}

export interface FactureVenteRapprochee {
  facture: FactureVenteRow;
  montant: number;
  isAvoir: boolean;
}

/** Récupère toutes les factures/avoirs rapprochés d'une transaction. */
export async function fetchFacturesVenteByTransactionId(
  transactionId: string
): Promise<FactureVenteRapprochee[]> {
  const { data: paiements, error: pErr } = await supabase
    .from("facture_paiements")
    .select("facture_id, montant")
    .eq("transaction_id", transactionId);
  if (pErr || !paiements?.length) return [];

  const result: FactureVenteRapprochee[] = [];
  for (const p of paiements) {
    const { data: f, error: fErr } = await supabase
      .from("factures")
      .select("id, numero, montant_ttc, restant_du_ttc, date_emission, facture_origine_id, clients(nom)")
      .eq("id", p.facture_id)
      .single();
    if (fErr || !f) continue;
    const clients = (f as { clients?: { nom: string }[] | { nom: string } | null }).clients;
    const client = Array.isArray(clients) ? clients[0] ?? null : clients ?? null;
    const isAvoir = !!(f as { facture_origine_id?: string | null }).facture_origine_id;
    result.push({
      facture: {
        id: f.id,
        numero: f.numero,
        montant_ttc: Number(f.montant_ttc) || 0,
        restant_du_ttc: Number(f.restant_du_ttc) || 0,
        date_emission: f.date_emission,
        client,
        isAvoir,
      },
      montant: Number(p.montant) || 0,
      isAvoir,
    });
  }
  return result;
}

/** Avoirs émis sur une facture (facture_origine_id = factureId). */
export async function fetchAvoirsLiesFactureVente(
  factureId: string
): Promise<Array<{ id: string; numero: string; montant_ttc: number; restant_du_ttc: number; date_emission: string }>> {
  const { data, error } = await supabase
    .from("factures")
    .select("id, numero, montant_ttc, restant_du_ttc, date_emission")
    .eq("facture_origine_id", factureId)
    .order("date_emission", { ascending: false });
  if (error) return [];
  return (data || []).map((f) => ({
    id: f.id,
    numero: f.numero,
    montant_ttc: Number(f.montant_ttc) || 0,
    restant_du_ttc: Number(f.restant_du_ttc) || 0,
    date_emission: f.date_emission,
  }));
}

/** Récupère la première facture client liée (compatibilité legacy, une seule). */
export async function fetchFactureVenteByTransactionId(
  transactionId: string
): Promise<FactureVenteRow | null> {
  const list = await fetchFacturesVenteByTransactionId(transactionId);
  return list[0]?.facture ?? null;
}

/** Détache un rapprochement (supprime les facture_paiements facture+transaction et remet le restant). */
export async function detacherFactureVente(factureId: string, transactionId: string): Promise<void> {
  const { data: paiements, error: pErr } = await supabase
    .from("facture_paiements")
    .select("montant")
    .eq("facture_id", factureId)
    .eq("transaction_id", transactionId);
  if (pErr || !paiements?.length) return;

  const totalDetache = paiements.reduce((s, p) => s + Number(p.montant) || 0, 0);

  const { error: delErr } = await supabase
    .from("facture_paiements")
    .delete()
    .eq("facture_id", factureId)
    .eq("transaction_id", transactionId);
  if (delErr) throw new Error(delErr.message || "Erreur lors du détachement");

  const { data: facture, error: fetchErr } = await supabase
    .from("factures")
    .select("restant_du_ttc, facture_origine_id")
    .eq("id", factureId)
    .single();
  if (fetchErr || !facture) return;

  const restant = Number(facture.restant_du_ttc) || 0;
  const isAvoir = !!facture.facture_origine_id;
  const nouveauRestant = isAvoir ? restant - totalDetache : restant + totalDetache;
  const payload: { restant_du_ttc: number; statut?: FactureStatut } = { restant_du_ttc: nouveauRestant };
  if (!isAvoir && nouveauRestant > 0 && Number(facture.restant_du_ttc) < 1) payload.statut = "envoye";

  await supabase.from("factures").update(payload).eq("id", factureId);
}

/** Récupère une facture publique par son share_token (accessible sans auth) */
export async function fetchFacturePublic(token: string): Promise<{
  facture: Facture & { clients?: Client };
  client: Client | null;
  lignes: FactureLigne[];
  dossier_infos: import("./dossiers").DossierInfos | null;
} | null> {
  const { data, error } = await supabase.rpc("get_facture_public", { p_token: token });
  if (error || !data) return null;
  const d = data as { facture: Facture & { clients?: Client }; client: Client | null; lignes: FactureLigne[]; dossier_infos: import("./dossiers").DossierInfos | null };
  return d;
}
